import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { Observable, ReplaySubject, catchError, mergeMap, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { UserResponse, UserWithPermissions } from './users.service';
import { RoleType } from './roles.service';
import { TenantsService, BasicTenantInfoResponse } from './tenants.service';
import { ErrorService } from './error.service';

type SendEmailResponse = {
  email_sent: boolean;
};

export type SignInError =
  | 'email_not_verified'
  | 'missing_2fa'
  | 'user_not_found'
  | 'other';
export type SignInStatus = {
  success: boolean;
  error?: SignInError;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserInfoUrl =
    environment.serverUrl + '/users/get-current-user';
  private sendEmailVerificationEmailUrl =
    environment.serverUrl + '/users/send-email-verification';
  private sendPasswordResetEmailUrl =
    environment.serverUrl + '/users/send-password-reset';
  currentUser?: UserWithPermissions;
  currentTenantInfo?: BasicTenantInfoResponse;

  isInSignUpFlow = false;

  public UNAUTHENTICATED_REDIRECT = '/login';
  public AUTHENTICATED_REDIRECT = '/';
  public authReady = new ReplaySubject<boolean>(1);

  constructor(
    private errorService: ErrorService,
    private firebaseService: FirebaseService,
    private http: HttpClient,
    private router: Router,
    private tenantsService: TenantsService
  ) {
    this.initializeAuth();
  }

  initializeAuth() {
    console.log('AuthService: Initializing auth');
    this.firebaseService.authReady
      .pipe(
        mergeMap((authReady) => {
          console.log('AuthService: Auth ready: ', authReady);
          if (!authReady) {
            return of(false);
          }

          // if no user signed in, or user not email verfied with 2FA method,
          // no need to fetch user info
          if (
            !this.firebaseService.signedIn ||
            !this.emailVerified ||
            !this.has2FAEnrollment
          ) {
            console.log('AuthService.initializeAuth: No user signed in');
            return of(true);
          }

          console.log('AuthService.initializeAuth: Fetching user info');
          return this.getCurrentUserInfo().pipe(
            mergeMap((status: SignInStatus) => {
              console.log('AuthService.getCurrentUserInfo status: ', status);
              return of(true);
            }),
            catchError((err) => {
              console.error(
                'AuthService.getCurrentUserInfo error: ',
                new Error(err)
              );
              return of(true);
            })
          );
        })
      )
      .subscribe((res) => {
        console.log('AuthService: Auth initialized - res: ', res);
        // on initial load, set user to be in sign up flow
        // this is to prevent the user from being logged out when first loading
        // the page
        this.isInSignUpFlow = true;
        this.authReady.next(res);
      });
  }

  get signedIn() {
    const signedIn =
      this.currentUser !== undefined &&
      this.firebaseService.currentUser !== null &&
      this.emailVerified &&
      this.has2FAEnrollment;

    // if (!signedIn) {
    //   console.log('AuthService: signedIn: ', signedIn, {
    //     currentUser: this.currentUser,
    //     firebaseUser: this.firebaseService.currentUser,
    //     emailVerified: this.emailVerified,
    //     has2FAEnrollment: this.has2FAEnrollment,
    //   });
    // }

    return signedIn;
  }

  get emailVerified() {
    if (!this.firebaseService.currentUser) {
      return false;
    }
    return this.firebaseService.currentUser.emailVerified;
  }

  get has2FAEnrollment() {
    if (!this.firebaseService.mfaUser?.enrolledFactors) {
      return false;
    }
    return this.firebaseService.mfaUser.enrolledFactors.length > 0;
  }

  get _2FAEnrollments() {
    if (!this.firebaseService.mfaUser?.enrolledFactors) {
      return undefined;
    }
    return this.firebaseService.mfaUser.enrolledFactors;
  }

  get hasApiSubscription() {
    if (!this.currentTenantInfo) {
      return false;
    }
    return this.currentTenantInfo.has_api_subscription;
  }

  isAdmin() {
    return (
      this.currentUser?.permissions.find(
        (p) => p.role.name === 'internal_admin' || p.role.name === 'super_admin'
      ) !== undefined
    );
  }

  canAccessPricing() {
    return (
      this.checkPermission('internal_admin') ||
      this.checkPermission('internal_sales') ||
      this.checkPermission('super_admin')
    );
  }

  isActivationKeysAdmin() {
    return (
      this.isSuperAdmin() ||
      this.currentUser?.permissions.some(
        (p) => p.role.name === 'activation_key_admin'
      ) === true
    );
  }

  isRegistrationDbAdmin() {
    return (
      this.isSuperAdmin() ||
      this.currentUser?.permissions.some(
        (p) => p.role.name === 'registration_db_admin'
      ) === true
    );
  }

  isSuperAdmin() {
    return (
      this.currentUser?.permissions.find(
        (p) => p.role.name === 'super_admin'
      ) !== undefined
    );
  }

  isInternal() {
    return (
      this.currentUser?.permissions.find(
        (p) =>
          p.role.name === 'internal_user' ||
          p.role.name === 'internal_admin' ||
          p.role.name === 'super_admin'
      ) !== undefined
    );
  }

  signInWithEmail(username: string, password: string) {
    return this.firebaseService.signInWithEmail(username, password).pipe(
      mergeMap((userCredential) => {
        return this.getCurrentUserInfo();
      })
    );
  }

  signInWithGoogle() {
    return this.firebaseService.signInWithGoogle().pipe(
      mergeMap((userCredential) => {
        return this.getCurrentUserInfo();
      })
    );
  }

  signOut(redirect = true) {
    console.log(`auth.service -> signOut(${redirect})`);
    return this.firebaseService.signOut().pipe(
      tap({
        next: () => {
          console.log('Signing out');
          this.currentUser = undefined;
          if (redirect) {
            this.router.navigate([this.UNAUTHENTICATED_REDIRECT]);
          }
        },
        error: (err) => {
          console.error(new Error(err));
          this.currentUser = undefined;
          if (redirect) {
            this.router.navigate([this.UNAUTHENTICATED_REDIRECT]);
          }
        },
      }),
      catchError((err) => {
        console.error(err);
        this.currentUser = undefined;
        if (redirect) {
          this.router.navigate([this.UNAUTHENTICATED_REDIRECT]);
        }
        return of();
      })
    );
  }

  checkPermission(roleType: RoleType) {
    if (!this.currentUser) {
      return false;
    }

    return this.currentUser.permissions.some((p) => p.role.name === roleType);
  }

  hasAnyPermissions(roleTypes: RoleType[]) {
    if (!this.currentUser) {
      return false;
    }

    return this.currentUser.permissions.some((p) =>
      roleTypes.includes(p.role.name)
    );
  }

  /** Fetches the user data from the server, and updates the current user on
   * the service.
   */
  getCurrentUserInfo(): Observable<SignInStatus> {
    // fetch user data from server
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .get<UserResponse>(this.currentUserInfoUrl, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          })
          .pipe(
            mergeMap((res) => {
              console.log('User from server: ', res);
              this.currentUser = res.user;
              return this.tenantsService
                .getBasicTenantInfo(res.user.tenant_id)
                .pipe(
                  mergeMap((res) => {
                    console.log('Tenant from server: ', res);
                    this.currentTenantInfo = res;
                    return of({
                      success: true,
                    });
                  })
                );
            }),
            catchError((err) => {
              console.error(err);
              if (err instanceof HttpErrorResponse && err.status === 401) {
                // if the user fails server auth, check if failure is due to
                // email verification or 2FA enrollment. If currently in sign up
                // flow, do not log user out.
                console.log('isInSignUpFlow: ', this.isInSignUpFlow);
                console.log('err.error?.error: ', err.error?.error);

                if (typeof err.error?.error !== 'string') {
                  return of({
                    success: false,
                    error: 'other' as SignInError,
                  });
                }

                if (
                  err.error.error.includes('email not verified') &&
                  this.isInSignUpFlow
                ) {
                  console.log('User is in sign up flow, not logging out');
                  return of({
                    success: false,
                    error: 'email_not_verified' as SignInError,
                  });
                } else if (
                  err.error.error.includes('missing 2fa') &&
                  this.isInSignUpFlow
                ) {
                  console.log('User is in sign up flow, not logging out');
                  return of({
                    success: false,
                    error: 'missing_2fa' as SignInError,
                  });
                } else if (
                  err.error.error.includes('user not found') ||
                  err.error.error.includes('user not active')
                ) {
                  // no user found on server, log user out
                  this.isInSignUpFlow = false;
                  this.signOut(true).subscribe();
                  return of({
                    success: false,
                    error: 'user_not_found' as SignInError,
                  });
                } else {
                  console.log('User is not authenticated, logging out');
                  this.isInSignUpFlow = false;
                  this.signOut(true).subscribe();
                  return of({
                    success: false,
                    error: 'other' as SignInError,
                  });
                }
              }
              return of({
                success: false,
                error: 'other' as SignInError,
              });
            })
          );
      }),
      catchError(this.errorService.handleObservableError<SignInStatus>())
    );
  }

  sendEmailVerificationEmail(email: string) {
    const url = this.sendEmailVerificationEmailUrl + '?email=' + email;
    return this.http.get<SendEmailResponse>(url);
  }

  sendPasswordResetEmail(email: string) {
    const url = this.sendPasswordResetEmailUrl + '?email=' + email;
    return this.http.get<SendEmailResponse>(url);
  }
}
